// Building an estimate in the office (estimates.save): its options (up to six, each with an optional
// tier: good, better, best) and their lines from the price book, or a line written in. A price
// different from the book says why; a price deep below the book or below cost waits for an OK (Q44),
// shown on the sheet once saved. A signed estimate is never edited here: the sheet offers Edit only on
// unsigned ones. The options stack one under another, so four to six read as well as one.

import { useEffect, useRef, useState } from 'react';
import type { MemberPlan } from '@flowology/domain';
import { client } from '../api.ts';
import { Money, errorText } from '../books/ui.tsx';
import { SaveButton, useSaveKey } from '../shared/SaveButton.tsx';
import type { JobDetail } from '../review/review-model.ts';
import { draftFromTemplate, type OptionTemplate } from '../settings/option-templates-model.ts';
import {
  blankOption,
  bookPriceFor,
  canAddOption,
  draftOf,
  draftProblems,
  freeLine,
  itemLine,
  lineCents,
  needsReason,
  newDraft,
  optionCents,
  saveInput,
  takeTodaysPrice,
  withQuantity,
  type Draft,
  type DraftLine,
  type DraftOption,
  type Estimate,
  type PricebookItem,
} from './job-model.ts';

const MAX_LINES = 60;

export function EstimateEditor({
  job,
  start,
  onCancel,
  onSaved,
}: {
  job: JobDetail;
  start: Estimate | 'new';
  onCancel: () => void;
  onSaved: (e: Estimate, isNew: boolean) => void;
}) {
  const isNew = start === 'new';
  const [d, setD] = useState<Draft>(() =>
    isNew ? newDraft(job.id, job.summary.slice(0, 200)) : draftOf(start),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  const [asked, setAsked] = useState(0); // each refused save brings the list of what to fix into view
  const problemsRef = useRef<HTMLDivElement>(null);
  const problems = draftProblems(d);
  useEffect(() => {
    if (asked) problemsRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [asked]);
  const save = useSaveKey(JSON.stringify(d));
  // a new estimate can start from a saved option set for this job (optionTemplates.list), priced today
  const [templates, setTemplates] = useState<OptionTemplate[]>([]);
  const [fromTemplate, setFromTemplate] = useState<string | null>(null);
  useEffect(() => {
    if (!isNew) return;
    client
      .call<{ templates: OptionTemplate[] }>('optionTemplates.list', { jobId: job.id })
      .then((r) => setTemplates(r.templates))
      .catch(() => setTemplates([]));
  }, [isNew, job.id]);
  const applyTemplate = (id: string) => {
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    const { options, skipped } = draftFromTemplate(t);
    if (!options.length)
      return setFromTemplate(`${t.name}: the price book no longer offers any of its items.`);
    setD((x) => ({ ...x, options }));
    setFromTemplate(
      `Started from ${t.name}, at today’s prices.${
        skipped
          ? ` ${skipped === 1 ? '1 line was' : `${skipped} lines were`} left out: the price book no longer offers ${skipped === 1 ? 'it' : 'them'}.`
          : ''
      }`,
    );
  };

  const setOption = (i: number, o: DraftOption) =>
    setD((x) => ({ ...x, options: x.options.map((y, k) => (k === i ? o : y)) }));

  const submit = async () => {
    setTried(true);
    if (problems.length) return setAsked((n) => n + 1);
    setBusy(true);
    setError(null);
    try {
      const e = await client.call<Estimate>('estimates.save', saveInput(d), { idempotencyKey: save.key });
      onSaved(e, isNew);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="est-editor"
      aria-labelledby="est-ed-title"
      onSubmit={(e) => {
        e.preventDefault();
        if (!busy) void submit();
      }}
    >
      <h3 id="est-ed-title">{isNew ? 'New estimate' : `Edit estimate ${start.number}`}</h3>
      <label className="f">
        Title
        <input
          className="field"
          maxLength={200}
          value={d.title}
          onChange={(e) => setD({ ...d, title: e.target.value })}
        />
      </label>

      {isNew && templates.length > 0 && (
        <label className="f">
          Start from a template
          <select
            className="field"
            value=""
            onChange={(e) => {
              if (e.target.value) applyTemplate(e.target.value);
            }}
          >
            <option value="">Choose one, or build the options below</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.options.length} option{t.options.length === 1 ? '' : 's'})
              </option>
            ))}
          </select>
        </label>
      )}
      {fromTemplate && (
        <p className="notice ok small" role="status">
          {fromTemplate}
        </p>
      )}

      {d.options.map((o, i) => (
        <OptionEditor
          key={o.optionId}
          option={o}
          index={i}
          plans={job.memberPlans ?? []}
          canRemove={d.options.length > 1}
          onChange={(x) => setOption(i, x)}
          onRemove={() => setD({ ...d, options: d.options.filter((_, k) => k !== i) })}
        />
      ))}

      {canAddOption(d) && (
        <div>
          <button
            type="button"
            className="btn soft"
            onClick={() => setD({ ...d, options: [...d.options, blankOption(d.options.length)] })}
          >
            Add an option
          </button>
        </div>
      )}

      {tried && problems.length > 0 && (
        <div className="notice warn" role="alert" ref={problemsRef}>
          <b>Before it can be saved:</b>
          <ul>
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="row end est-foot">
        <button type="button" className="btn soft" onClick={onCancel}>
          Back to the job
        </button>
        <SaveButton busy={busy} busyLabel="Saving…" error={error} onClick={() => void submit()}>
          Save the estimate
        </SaveButton>
      </div>
    </form>
  );
}

function OptionEditor({
  option: o,
  index,
  plans,
  canRemove,
  onChange,
  onRemove,
}: {
  option: DraftOption;
  index: number;
  /** The memberships at the job's address: lines from the book come in at the member's price. */
  plans: readonly MemberPlan[];
  canRemove: boolean;
  onChange: (o: DraftOption) => void;
  onRemove: () => void;
}) {
  const setLine = (i: number, l: DraftLine) =>
    onChange({ ...o, lines: o.lines.map((y, k) => (k === i ? l : y)) });
  const id = `opt-${o.optionId}`;
  return (
    <fieldset className="stone est-opt" aria-labelledby={`${id}-legend`}>
      <legend id={`${id}-legend`} className="sr-only">
        Option {index + 1}
      </legend>
      <div className="fgrid">
        <label className="f">
          Option {index + 1}
          <input
            className="field"
            maxLength={120}
            value={o.title}
            onChange={(e) => onChange({ ...o, title: e.target.value })}
          />
        </label>
        <label className="f">
          Tier
          <select
            className="field"
            value={o.tier}
            onChange={(e) => onChange({ ...o, tier: e.target.value as DraftOption['tier'] })}
          >
            <option value="">None</option>
            <option value="good">Good</option>
            <option value="better">Better</option>
            <option value="best">Best</option>
          </select>
        </label>
      </div>
      <label className="f">
        What it includes (optional)
        <textarea
          className="field area"
          rows={2}
          maxLength={2000}
          value={o.summary}
          onChange={(e) => onChange({ ...o, summary: e.target.value })}
        />
      </label>

      {o.lines.length > 0 && (
        <ol className="est-lines">
          {o.lines.map((l, i) => (
            <LineEditor
              key={l.lineId}
              line={l}
              onChange={(x) => setLine(i, x)}
              onRemove={() => onChange({ ...o, lines: o.lines.filter((_, k) => k !== i) })}
            />
          ))}
        </ol>
      )}

      {o.lines.length < MAX_LINES && (
        <PricebookPicker
          plans={plans}
          onPick={(item) => onChange({ ...o, lines: [...o.lines, itemLine(item, plans)] })}
          onFree={() => onChange({ ...o, lines: [...o.lines, freeLine()] })}
        />
      )}

      <div className="row between est-opt-foot">
        <span>
          Before tax <Money cents={optionCents(o)} />
        </span>
        {canRemove && (
          <button type="button" className="btn quiet" onClick={onRemove}>
            Remove this option
          </button>
        )}
      </div>
    </fieldset>
  );
}

function LineEditor({
  line: l,
  onChange,
  onRemove,
}: {
  line: DraftLine;
  onChange: (l: DraftLine) => void;
  onRemove: () => void;
}) {
  const free = l.pricebookItemId === null;
  const total = lineCents(l);
  const why = needsReason(l);
  return (
    <li className="est-line">
      <div className="est-line-grid">
        <label className="f est-desc">
          {free ? 'What it is' : 'Item'}
          <input
            className="field"
            maxLength={500}
            value={l.description}
            onChange={(e) => onChange({ ...l, description: e.target.value })}
          />
        </label>
        <label className="f est-qty">
          Qty
          <input
            className="field"
            inputMode="decimal"
            value={l.quantity}
            onChange={(e) => onChange(withQuantity(l, e.target.value))}
          />
        </label>
        <label className="f est-price">
          Price
          <input
            className="field"
            inputMode="decimal"
            value={l.price}
            onChange={(e) => onChange({ ...l, price: e.target.value })}
          />
        </label>
        {free && (
          <label className="f est-price">
            Our cost (optional)
            <input
              className="field"
              inputMode="decimal"
              value={l.cost}
              onChange={(e) => onChange({ ...l, cost: e.target.value })}
            />
          </label>
        )}
      </div>
      {!free && l.bookPriceCents !== null && (
        <p className="muted small">
          {l.member ? (
            <>
              Member price <Money cents={l.bookPriceCents} /> · {l.member.planName} · regular{' '}
              <Money cents={l.member.listPriceCents} />
            </>
          ) : (
            <>
              Book price <Money cents={l.bookPriceCents} />
            </>
          )}
        </p>
      )}
      {!free && l.bookNowCents != null && (
        // the customer saw this price: it stays unless someone takes today's on purpose
        <p className="small est-book-now">
          Book now says <Money cents={l.bookNowCents} /> ·{' '}
          <button type="button" className="btn quiet" onClick={() => onChange(takeTodaysPrice(l))}>
            Use today’s price
          </button>
        </p>
      )}
      {why && (
        <label className="f">
          {free ? 'Why (a line not from the price book needs a reason)' : 'Why the price changed'}
          <input
            className="field"
            maxLength={500}
            value={l.reason}
            onChange={(e) => onChange({ ...l, reason: e.target.value })}
          />
        </label>
      )}
      <div className="row between">
        <span className="small">{total === null ? '' : <Money cents={total} />}</span>
        <button
          type="button"
          className="btn quiet"
          onClick={onRemove}
          aria-label={`Remove ${l.description || 'this line'}`}
        >
          Remove
        </button>
      </div>
    </li>
  );
}

/** Search the price book and pick an item; with onFree, also "Write a line in instead" (option templates leave it out). */
export function PricebookPicker({
  onPick,
  onFree,
  label = 'Add from the price book',
  plans = [],
}: {
  onPick: (item: PricebookItem) => void;
  onFree?: () => void;
  label?: string;
  /** A member's address: each item shows the price the estimate will give it. */
  plans?: readonly MemberPlan[];
}) {
  const [query, setQuery] = useState('');
  const [found, setFound] = useState<PricebookItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!query.trim()) {
      setFound(null);
      return;
    }
    const t = setTimeout(() => {
      client
        .call<{ items: PricebookItem[] }>('pricebook.listItems', { query: query.trim(), limit: 8 })
        .then((r) => {
          setFound(r.items);
          setError(null);
        })
        .catch((e: unknown) => setError(errorText(e, 'Could not search the price book.')));
    }, 200);
    return () => clearTimeout(t);
  }, [query]);
  return (
    <div className="est-pick">
      <label className="f">
        {label}
        <input
          className="field"
          type="search"
          placeholder="Name or code"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </label>
      {error && <p className="error small">{error}</p>}
      {found && (
        <ul className="found" aria-label="Pricebook items">
          {found.length ? (
            found.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className="row-btn"
                  onClick={() => {
                    onPick(item);
                    setQuery('');
                  }}
                >
                  <span className="who-col">
                    <b>{item.name}</b> <span className="muted small">{item.code}</span>
                  </span>
                  <Money cents={bookPriceFor(item, plans).priceCents} />
                </button>
              </li>
            ))
          ) : (
            <li className="muted small">Nothing in the price book matches “{query.trim()}”.</li>
          )}
        </ul>
      )}
      {onFree && (
        <div>
          <button type="button" className="btn quiet" onClick={onFree}>
            Write a line in instead
          </button>
        </div>
      )}
    </div>
  );
}
